import type { StandardSchemaV1 } from 'node_modules/zod/v4/core/standard-schema';
import { UserSchema, UserUpdateSchema } from '@shared/modelTypes/user-types';
import z from 'zod';
import {
	LocationCreateSchema,
	LocationSchema,
	LocationUpdateSchema,
	LocationWithSessionsSchema,
	SetLocationSchema,
} from '@shared/modelTypes/location-types';
import { RoomSchema, RoomsUpdateSchema } from '@shared/modelTypes/room-types';
import {
	AbortRepairSchema,
	AssignTechToRoomSchema,
	ExtendRepairTimeSchema,
	SendChatMessageSchema,
	SetPlayerSchema,
	StartShiftSchema,
	ValidateQrCodeSchema,
} from '@shared/modelTypes/game-types';
import { MemberIdSchema, SessionIdSchema, TeamNameSchema } from '@shared/modelTypes/session-types';
import { GamePresetSchema } from '@shared/modelTypes/game-preset-types';

export type BaseApi = Record<string, { params?: StandardSchemaV1; returns?: StandardSchemaV1 }>;

export type ApiCallParams<API extends BaseApi, K extends keyof API> = API[K] extends { params: any }
	? StandardSchemaV1.InferOutput<API[K]['params']>
	: unknown;

export type ApiCallReturn<API extends BaseApi, K extends keyof API> = API[K] extends { returns: any }
	? StandardSchemaV1.InferOutput<API[K]['returns']>
	: unknown;

export type ApiClient<API extends BaseApi> = {
	[K in keyof API]: (args: ApiCallParams<API, K>) => Promise<ApiCallReturn<API, K>>;
};

export type CallContext<TContext extends Record<string, any>, TApi extends BaseApi, K extends keyof TApi> = TContext & {
	params: ApiCallParams<TApi, K>;
};

export type ApiImpl<TContext extends Record<string, any>, TApi extends BaseApi> = {
	[K in keyof TApi]: (ctx: CallContext<TContext, TApi, K>) => Promise<ApiCallReturn<TApi, K>>;
};

export type ApiClientConfig<TApi extends BaseApi> = {
	handler<K extends keyof TApi>(opts: { endpoint: K; params: ApiCallParams<TApi, K> }): Promise<ApiCallReturn<TApi, K>>;
};

function defineApi<const T extends BaseApi>(api: T) {
	return api;
}

function ResultSchema<T extends z.ZodTypeAny>(dataSchema: T) {
	return z.discriminatedUnion('success', [
		z.object({
			success: z.literal(true),
			message: z.string(),
			data: dataSchema,
		}),
		z.object({
			success: z.literal(false),
			message: z.string(),
		}),
	]);
}

function EventLogSchema() {
	return z.discriminatedUnion('eventType', [
		z.object({
			eventType: z.literal('JOIN_LOCATION'),
			data: z.object({
				success: z.boolean(),
				locationId: z.uuid().optional(),
				code: z.string(),
			}),
		}),
		z.object({
			eventType: z.literal('LEAVE_LOCATION'),
			playerId: z.string().optional(),
			sessionId: z.uuid().optional(),
			data: z.object({
				locationId: z.uuid(),
				deviceId: z.uuid(),
			}),
		}),
		z.object({
			eventType: z.literal('JOIN_SESSION'),
			playerId: z.string(),
			sessionId: z.uuid(),
			data: z.object({
				success: z.boolean(),
				deviceId: z.uuid(),
			}),
		}),
		z.object({
			eventType: z.literal('LEAVE_SESSION'),
			playerId: z.string(),
			sessionId: z.uuid(),
		}),
		z.object({
			eventType: z.literal('SHIFT_STARTED'),
			playerId: z.string(),
			sessionId: z.uuid(),
		}),
		z.object({
			eventType: z.literal('OPEN_ROOM'),
			data: z.object({
				roomId: z.uuid(),
				startupDuration: z.number(),
			}),
		}),
		z.object({
			eventType: z.literal('CONNECT_ROOM'),
			playerId: z.string(),
			sessionId: z.uuid(),
			data: z.object({
				roomId: z.uuid(),
				code: z.string(),
				progress: z.number(),
				repairDuration: z.number(),
			}),
		}),
		z.object({
			eventType: z.literal('REPAIR_ROOM'),
			playerId: z.string(),
			sessionId: z.uuid(),
			data: z.object({
				success: z.boolean(),
				roomId: z.uuid(),
				code: z.string(),
				progress: z.number(),
			}),
		}),
		z.object({
			eventType: z.literal('WRONG_SCAN'),
			playerId: z.string(),
			sessionId: z.uuid(),
			data: z.object({
				code: z.string(),
			}),
		}),
		z.object({
			eventType: z.literal('ABORT_REPAIR'),
			playerId: z.string(),
			sessionId: z.uuid(),
			data: z.object({
				roomId: z.uuid(),
				progress: z.number(),
			}),
		}),
		z.object({
			eventType: z.literal('ADD_TIME'),
			playerId: z.string(),
			sessionId: z.uuid(),
			data: z.object({
				roomId: z.uuid(),
				progress: z.number(),
			}),
		}),
		z.object({
			eventType: z.literal('FAILED_ROOM'),
			playerId: z.string(),
			sessionId: z.uuid(),
			data: z.object({
				roomId: z.uuid(),
			}),
		}),
		z.object({
			eventType: z.literal('RESET_CONNECTION'),
			playerId: z.string(),
			sessionId: z.string(),
			data: z.object({
				code: z.string(),
				progress: z.number(),
			}),
		}),
		z.object({
			eventType: z.literal('LOST_CONNECTION'),
			playerId: z.string(),
			sessionId: z.string(),
		}),
		z.object({
			eventType: z.literal('BONUS_CODE'),
			playerId: z.string(),
			sessionId: z.uuid(),
			data: z.object({
				code: z.string(),
			}),
		}),
	]);
}

export const api = defineApi({
	// session
	createSession: {
		params: z.object({ locationId: z.string() }),
	},
	runSession: {
		params: z.object({ sessionId: SessionIdSchema }),
	},
	stopSession: {
		params: z.object({ sessionId: SessionIdSchema }),
	},
	openRandomRoom: {
		params: z.object({ sessionId: SessionIdSchema }),
	},

	// code validation
	getLocationByCode: {
		params: SetLocationSchema,
		returns: ResultSchema(LocationSchema),
	},

	// set Player
	setPlayer: {
		params: SetPlayerSchema,
		returns: ResultSchema(z.undefined()),
	},

	startShift: { params: StartShiftSchema, returns: ResultSchema(z.undefined()) },
	abortRepair: { params: AbortRepairSchema, returns: ResultSchema(z.undefined()) },
	extendRepairTime: { params: ExtendRepairTimeSchema, returns: ResultSchema(z.undefined()) },
	assignTechToRoom: {
		params: AssignTechToRoomSchema,
		returns: ResultSchema(z.undefined()),
	},
	validateConnectionCode: {
		params: ValidateQrCodeSchema,
		returns: ResultSchema(z.undefined()),
	},
	validateCode: {
		params: ValidateQrCodeSchema,
		returns: ResultSchema(z.undefined()),
	},

	// chat
	sendChatMessage: {
		params: SendChatMessageSchema,
	},
	memberDelete: { params: z.object({ sessionId: z.uuid(), memberId: MemberIdSchema }), returns: ResultSchema(z.undefined()) },

	// // user
	// userCreate: { params: UserCreateSchema, returns: UserSchema },
	userGet: { params: z.object({ id: z.string() }), returns: ResultSchema(UserSchema) },
	userList: { params: z.object({}), returns: ResultSchema(z.array(UserSchema)) },
	userUpdateById: { params: UserUpdateSchema, returns: ResultSchema(UserSchema) },
	// userUpdate: { params: z.object({ id: z.string(), user: UserSchema }), returns: UserSchema },
	// userDelete: { params: z.object({ id: z.string() }), returns: z.null() },

	// location
	locationCreate: { params: LocationCreateSchema, returns: ResultSchema(LocationSchema) },
	locationGet: { params: z.object({ id: z.uuid() }), returns: ResultSchema(LocationSchema) },
	locationList: { returns: ResultSchema(z.array(LocationSchema)) },
	locationUpdate: { params: LocationUpdateSchema, returns: ResultSchema(LocationSchema) },
	locationDuplicate: { params: z.object({ id: z.uuid() }), returns: ResultSchema(LocationSchema) },
	locationDelete: { params: z.object({ id: z.uuid() }), returns: ResultSchema(z.null()) },
	locationGetWithSessionList: { params: z.object({ id: z.uuid() }), returns: ResultSchema(LocationWithSessionsSchema) },
	dashboardData: {
		returns: ResultSchema(
			z.object({
				locations: z.array(LocationSchema),
				locationCount: z.number(),
				locationIncrease: z.number(),
				sessionCount: z.number(),
				sessionIncrease: z.number(),
				roomCount: z.number(),
			}),
		),
	},

	gamePresetList: { returns: ResultSchema(z.array(GamePresetSchema)) },
	gamePresetGet: { params: z.object({ id: z.uuid() }), returns: ResultSchema(GamePresetSchema) },
	gamePresetUpdate: { params: GamePresetSchema, returns: ResultSchema(GamePresetSchema) },

	// room
	roomList: { returns: ResultSchema(z.array(RoomSchema)) },
	roomsUpdate: { params: RoomsUpdateSchema, returns: ResultSchema(z.array(RoomSchema)) },

	// report
	generateReport: { params: z.object({ id: z.uuid() }), returns: ResultSchema(z.string()) },
	generateGameExport: { returns: ResultSchema(z.string()) },

	// session export
	sessionExport: {
		returns: ResultSchema(
			z.array(
				z.object({
					sessionId: z.string(),
					locationName: z.string(),
					date: z.string().nullable(),
					totalScore: z.number(),
					systemDamage: z.number(),
					durationMinutes: z.number().nullable(),
					totalRoomsConnected: z.number(),
					totalRepairsSuccessful: z.number(),
					totalRepairsAborted: z.number(),
					totalTimeExtensions: z.number(),
					totalBonusCodes: z.number(),
					totalConnectionsLost: z.number(),
					totalConnectionsRestored: z.number(),
					teams: z.record(
						TeamNameSchema,
						z.object({
							score: z.number(),
							roomsConnected: z.number(),
							repairsSuccessful: z.number(),
							repairsAborted: z.number(),
							timeExtensions: z.number(),
							bonusCodes: z.number(),
							connectionsLost: z.number(),
							connectionsRestored: z.number(),
						}),
					),
				}),
			),
		),
	},

	// ranking
	rankingData: {
		returns: ResultSchema(
			z.object({
				topSessions: z.array(
					z.object({
						sessionId: z.string(),
						locationName: z.string(),
						totalScore: z.number(),
						date: z.string().nullable(),
					}),
				),
				topCrews: z.array(
					z.object({
						teamName: TeamNameSchema,
						bestScore: z.number(),
						locationName: z.string(),
						date: z.string().nullable(),
					}),
				),
				topSessionsByDamage: z.array(
					z.object({
						sessionId: z.string(),
						locationName: z.string(),
						systemDamage: z.number(),
						date: z.string().nullable(),
					}),
				),
			}),
		),
	},

	// event logs
	eventLogCreate: { params: EventLogSchema(), returns: ResultSchema(z.undefined()) },
});

export type MonteursGameApi = typeof api;

export const socket = defineApi({
	goodbye: { params: z.object({ name: z.string() }), returns: z.string() },
});

export type MonteursGameSocket = typeof socket;
